import {
  Component,
  Input,
  OnInit,
  forwardRef,
  ChangeDetectorRef,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  ViewChild,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import {
  AppCountry,
  EstimateLabels,
  Labels,
  SdfCardLabels,
} from '../../app.models';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { StringUtils } from '../../shared/utils/string-utils';
import { CountryObj } from '../../store/types';
import { config as countryConfig } from '../../config/country-config';
import { Store } from '@ngrx/store';
import {
  FetchServiceOptions,
  ServiceOptionsRequest,
} from '../../store/service-options';
import { UtilsService } from '../../services/utils.service';
import { defaultCountry } from '../../store/service-options/service-options.selectors';
import {
  fadeInOutAnimation,
  dropdownAnimation,
  overlayFadeInOutAnimation,
  slideInOutAnimation,
} from '../../shared/reusable-animations/card-estimate-drawer-animations';
import { MobileBreakPointsService } from 'src/app/services/mobileBreakPoints.service';
import { FlagService } from 'src/app/services/flag.service';
export interface CountryData {
  countryCode: string;
  defaultCountry: string;
  isSupportedCountry: boolean;
}
@Component({
  selector: 'app-custom-country-input-selector',
  templateUrl: './custom-country-input-selector.component.html',
  styleUrls: ['./custom-country-input-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomCountryInputSelectorComponent),
      multi: true,
    },
  ],
  animations: [
    fadeInOutAnimation,
    dropdownAnimation,
    overlayFadeInOutAnimation,
    slideInOutAnimation,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomCountryInputSelectorComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  private _isExpandedBhaviorSubject$: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  isExpanded$: Observable<boolean> =
    this._isExpandedBhaviorSubject$.asObservable();
  // isExpanded: boolean = false;
  isDrawerVisible: boolean = false;
  isClickHandlerActive: boolean = false;
  countryControl: FormControl<string | null> = new FormControl('', [
    Validators.required,
  ]);
  @Output() countryDataEmitter: EventEmitter<CountryData> =
    new EventEmitter<CountryData>();
  isDisabled: boolean = false;
  isUserTyping: boolean = false;
  private _value: string;
  private destroy$: Subject<void> = new Subject<void>();
  onChange: Function = (value: string) => {};
  onTouch: Function = () => {};
  estimateLabels: EstimateLabels;
  _labels: any;
  csmCountries: any[];
  defaultCountry: string;
  filteredCountries$: Observable<CountryObj[]>;
  isMobile$: Observable<boolean>;
  dropdownHeight: string = '527px';
  countries: AppCountry[] = countryConfig;
  sdfCardLabels: SdfCardLabels;
  flagSrc: string;
  flagUrls: { [key: string]: string } = {};
  @Input() countryCode: string | undefined;
  @Input() token!: string;
  @Input() locale!: string;
  @Input() countryCodes: string[];
  @Input() countriesFromStore: any;
  @Input() disableCashInCountry: boolean;
  @Input() flow: string;
  activeIndex: number = -1;
  @Input() set labels(labels: Labels) {
    this._labels = labels;
    this.estimateLabels = labels.stellar.estimate;
    this.sdfCardLabels = labels.stellar.sdfcard;
    this.csmCountries = this.countries.map(
      (country) => labels?.stellar?.country[country?.code]
    );
  }
  private _countryName!: string;
  @Input() set countryName(countryName: string | undefined) {
    this._countryName = countryName!;
    this.countryControl.setValue(this._countryName);
  }

  get countryName() {
    return this._countryName;
  }

  public get labels() {
    return this._labels;
  }

  private _endpoint: string;
  @Input() public set endpoint(endpointValue: string) {
    this._endpoint = endpointValue;
  }
  public get endpoint() {
    return this._endpoint;
  }

  @ViewChild('containerWrapper', { static: false, read: ElementRef })
  containerWrapper!: ElementRef;
  @HostListener('document:click', ['$event'])
  handleClickOutside(event: Event) {
    const isExandedValue = this._isExpandedBhaviorSubject$.getValue();
    if (
      isExandedValue &&
      this.containerWrapper &&
      this.isClickHandlerActive &&
      !this.containerWrapper.nativeElement.contains(event.target)
    ) {
      this._reseCountryControlAndSelectedCountryName();
      this._isExpandedBhaviorSubject$.next(false);
      this.isDrawerVisible = false;
      this.setCountryFlagsUrl(this.countriesFromStore);
    }
  }
  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      if (this.activeIndex < this.countriesFromStore.length - 1) {
        this.activeIndex++;
      }
    } else if (event.key === 'ArrowUp') {
      if (this.activeIndex > 0) {
        this.activeIndex--;
      }
    }
  }
  @HostListener('window:wheel', ['$event'])
  onScroll(event: WheelEvent) {
    const elementType: string[] = ['text', 'number'];
    const activeElement = document.activeElement as HTMLInputElement;
    if (activeElement && elementType.includes(activeElement.type)) {
      activeElement.blur();
    }
  }

  constructor(
    private store: Store<any>,
    private cdr: ChangeDetectorRef,
    private _elementRef: ElementRef,
    private utilService: UtilsService,
    private mobileBreakPoints: MobileBreakPointsService,
    private flagService: FlagService
  ) {
    this.isClickHandlerActive = true;
  }

  ngOnInit(): void {
    document.documentElement.style.setProperty('--height-country-sel', '122px');
    this.isMobile$ = this.mobileBreakPoints
      .mobileBreakPoints$ as Observable<boolean>;
    this.countryControl.valueChanges?.subscribe((value) => {
      this.onChange(value);
      this.onTouch();
    });

    this.store.select(defaultCountry).subscribe((defaultCountryCode) => {
      this.defaultCountry = defaultCountryCode;
      if (this.defaultCountry) {
        this.flagSrc = this.flagService.getSelectedFlag(defaultCountryCode);
      }
    });

    this.filteredCountries$ = this.countryControl.valueChanges.pipe(
      startWith(''),
      map((value) => this.countriesFilter(value || '')),
      tap((filteredCountries) => {
        this.adjustDropdownHeight(filteredCountries);
      })
    );

    combineLatest([this.filteredCountries$, this.countryControl.valueChanges])
      .pipe(debounceTime(0), distinctUntilChanged())
      .subscribe(([country, value]: [CountryObj[], string | null]) => {
        this.setCountryFlagsUrl(country);
        const filterValue = StringUtils.removeDiacriticsAndConvertToLowercase(
          value!
        );
        if (filterValue && !country.length) {
          this.countryControl?.setErrors({ invalidOption: true });
        } else if (!filterValue && country.length) {
          this.countryControl.setErrors({ required: true });
        } else if (
          filterValue ===
          StringUtils.removeDiacriticsAndConvertToLowercase(country[0]?.name)
        ) {
          const matchedOption = country.filter(
            (country: CountryObj) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) === filterValue
          );
          if (matchedOption.length > 1) {
            const selectedOption =
              matchedOption.find(
                (country: CountryObj) =>
                  StringUtils.removeDiacriticsAndConvertToLowercase(
                    country?.name
                  ) === 'united states of america'
              ) || matchedOption[0];
            this.countryControl.setValue(selectedOption?.name, {
              emitEvent: false,
            });
            this.onChange(selectedOption.code);
            this.setDefaultCountryStatePair(selectedOption?.name);
            this._isExpandedBhaviorSubject$.next(false);
            this.countryControl.setErrors(null);
          } else if (matchedOption.length === 1) {
            this.countryControl.setValue(matchedOption[0]?.name, {
              emitEvent: false,
            });
            this.onChange(matchedOption[0]?.code);
            this.setDefaultCountryStatePair(matchedOption[0]?.name);
            this._isExpandedBhaviorSubject$.next(false);
            this.countryControl.setErrors(null);
          }
          this.countryControl.setErrors(null);
        } else {
          this.countryControl.setErrors({ invalidOption: true });
        }
      });
    this.setMobileView(this.isMobile$);
    this.filteredCountries$.subscribe((filterCountries) => {
      this.setCountryFlagsUrl(filterCountries);
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  adjustDropdownHeight(filteredCountries: AppCountry[]): void {
    switch (filteredCountries.length) {
      case 0:
        this.dropdownHeight = '144px';
        break;
      case 1:
        this.dropdownHeight = '144px';
        break;
      case 2:
        this.dropdownHeight = '200px';
        break;
      case 3:
        this.dropdownHeight = '256px';
        break;
      case 4:
        this.dropdownHeight = '312px';
        break;
      case 5:
        this.dropdownHeight = '368px';
        break;
      case 6:
        this.dropdownHeight = '424px';
        break;
      case 7:
        this.dropdownHeight = '480px';
        break;
      default:
        const estimatedHeightPerItem = 527;
        const computedHeight =
          filteredCountries.length * estimatedHeightPerItem;
        this.dropdownHeight = `${Math.min(computedHeight, 527)}px`;
        break;
    }
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  writeValue(obj: any): void {
    if (obj !== undefined && obj !== null) {
      this._value = obj;
      this.countryControl.setValue(this.getCountryName(this._value));
      this._countryName = this.countryControl.value!;
      this.countryCode = this._value;
      this.onTouch();
    }
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  setDefaultCountryStatePair(countryName: string | null) {
    this._countryName = countryName!;
    this._value = countryName!;
    this.onChange(this._value);
    this.countryControl.setValue(countryName, { emitEvent: false });
    this.setCountryFlagsUrl(this.countriesFromStore);
    this._isExpandedBhaviorSubject$.next(false);
    this.isDrawerVisible = false;
    if (countryName) {
      const countryCode = this.getCountryCode(countryName);
      const isSupportedCountry: any = this.countryCodes.find(
        (code) => code === countryCode
      );
      if (
        countryCode &&
        this.defaultCountry !== countryCode &&
        isSupportedCountry
      ) {
        this.onCountryChange(countryCode);
        this.onChange(this._value);
      }
      const countryData: CountryData = {
        countryCode: countryCode,
        defaultCountry: this.defaultCountry,
        isSupportedCountry: isSupportedCountry,
      };
      this.countryDataEmitter.emit(countryData);
    }
    this.isUserTyping = false;
  }
  public onCountryChange(selectedCountry: any) {
    if (
      this.defaultCountry !== selectedCountry &&
      (this.flow === 'withdraw' || !this.disableCashInCountry)
    ) {
      const payload: ServiceOptionsRequest = {
        token: this.token,
        language: this.locale?.substring(0, 2),
        operation: this.flow,
        selectedWithdrawCountry: selectedCountry,
      };
      this.countryControl.setValue(selectedCountry);
      this.store.dispatch(
        FetchServiceOptions({
          getOptionsRequest: payload,
          endpoint: this.endpoint,
        })
      );
    }
  }

  getCountryCode(countryValue: string): any {
    const countryCode =
      this.csmCountries && countryValue
        ? this.csmCountries.find(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country?.name
              ) ==
              StringUtils.removeDiacriticsAndConvertToLowercase(countryValue)
          )?.code
        : '';
    if (countryCode) {
      return countryCode;
    } else {
      return this.csmCountries && countryValue
        ? this.csmCountries.find(
            (country: any) =>
              StringUtils.removeDiacriticsAndConvertToLowercase(
                country.code
              ) ===
              StringUtils.removeDiacriticsAndConvertToLowercase(countryValue)
          )?.code
        : '';
    }
  }

  getCountryName(value: string): string {
    // Make sure value has country code not name
    if (value?.length > 3 && this.countryCode) {
      value = this.countryCode;
    }
    const country = this.countriesFromStore.find(
      (country: CountryObj) => country.code === value
    );

    return country && country.name ? country.name : '';
  }

  private countriesFilter(value: any): AppCountry[] {
    const filterValue =
      StringUtils.removeDiacriticsAndConvertToLowercase(value);
    return this.countriesFromStore.filter(
      (option: { name: string; code: string }) => {
        const resultFilteredByName =
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.name
          ).startsWith(filterValue);
        const resultFilteredByCode =
          StringUtils.removeDiacriticsAndConvertToLowercase(
            option?.name
          ).startsWith(filterValue);
        return (
          (option?.name && resultFilteredByName) ||
          (option?.code && resultFilteredByCode)
        );
      }
    );
  }
  getFlagUrls(countryName: string): string {
    const flagUrls = this.flagUrls.hasOwnProperty(countryName)
      ? this.flagUrls[countryName]
      : 'assets/images/flag-placeholder.svg';
    return flagUrls;
  }
  setCountryFlagsUrl(countries: CountryObj[]) {
    this.flagUrls = this.flagService.getAllFlags(countries);
  }
  private setMobileView(isMobile$: Observable<boolean>): void {
    isMobile$.pipe(takeUntil(this.destroy$)).subscribe((isMobile) => {
      if (isMobile) {
        this.isExpanded$.subscribe((isExpanded) => {
          if (isExpanded) {
            this.isDrawerVisible = true;
          } else {
            this.isDrawerVisible = false;
          }
        });
      } else {
        this.isExpanded$.subscribe((isExpanded) => {
          if (isExpanded) {
            this.isDrawerVisible = false;
          } else {
            this.isDrawerVisible = true;
          }
        });
      }
    });
  }

  toggleContent() {
    const currentValue = this._isExpandedBhaviorSubject$.getValue();
    this._isExpandedBhaviorSubject$.next(!currentValue);
    this.isDrawerVisible = !this.isDrawerVisible;
    this.setCountryFlagsUrl(this.countriesFromStore);
  }

  closeDrawer() {
    this._reseCountryControlAndSelectedCountryName();
    this.isDrawerVisible = false;
    this._isExpandedBhaviorSubject$.next(false);
    this.setCountryFlagsUrl(this.countriesFromStore);
  }

  private _reseCountryControlAndSelectedCountryName() {
    const countryControl = this.countryControl;
    if (!countryControl) return;
    const countryInputValue = StringUtils.removeDiacriticsAndConvertToLowercase(
      countryControl?.value ?? ''
    );
    const foundCountry = this.csmCountries.some(
      (country) =>
        StringUtils.removeDiacriticsAndConvertToLowercase(country?.name) ===
          countryInputValue ||
        StringUtils.removeDiacriticsAndConvertToLowercase(country?.iso2Code) ===
          countryInputValue
    );
    if (!foundCountry || !countryInputValue) {
      countryControl.setValue('');
      this._countryName = '';
    }
  }

  onResetForm() {
    this.countryControl.reset();
    this._countryName = '';
    this.isUserTyping = false;
  }

  displayCountrySelContent() {
    const display = !this.isUserTyping && this.countryControl.value!;
    if (!display) {
      document.documentElement.style.setProperty('--height-country-sel', '0px');
    } else {
      document.documentElement.style.setProperty(
        '--height-country-sel',
        '122px'
      );
    }
    return display;
  }
}
